/**
 * The search field, with type-ahead.
 *
 * **Shaped to match the server-rendered catalogue.** Those pages carry no
 * JavaScript, so their search is a plain GET form: a pill-shaped input with the
 * submit button fused to its right edge. A reader crossing between a catalogue
 * page and an app page sees the same header, and the same search field, rather
 * than two products wearing one logo — the same reason the theme cookie is
 * shared between them.
 *
 * Matching the *shape* only. The type-ahead, the abort-on-change and the
 * keyboard handling below are all things the catalogue's form cannot do and
 * should not lose here.
 *
 * Search is the front door of this product, so the details matter:
 *
 * - Suggestions are debounced and the in-flight request is **aborted** when the
 *   query changes. Without that, a fast typist's results arrive out of order
 *   and the list flickers between stale answers.
 * - The whole thing is keyboard-navigable, including ↑/↓/Enter/Escape, because
 *   the people who use a supplier index all day will not reach for a mouse.
 * - `⌘K` / `Ctrl-K` focuses it from anywhere.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BuildingIcon, PackageIcon, SearchIcon, TagIcon } from '@/components/ui/icons'
import { cx, Spinner } from '@/components/ui/primitives'
import { searchApi } from '@/services/endpoints'
import type { Suggestion } from '@/types/api'

const DEBOUNCE_MS = 160
const MIN_QUERY = 2

const TYPE_ICON = {
  business: BuildingIcon,
  product: PackageIcon,
  category: TagIcon,
} as const

const TYPE_PATH = {
  business: (slug: string) => `/businesses/${slug}`,
  product: (slug: string) => `/products/${slug}`,
  category: (slug: string) => `/search?category=${encodeURIComponent(slug)}`,
} as const

interface SearchBarProps {
  autoFocus?: boolean
  placeholder?: string
  size?: 'md' | 'lg'
  initialValue?: string
  className?: string
}

export function SearchBar({
  autoFocus,
  placeholder = 'Search businesses, products, suppliers or services…',
  size = 'md',
  initialValue = '',
  className,
}: SearchBarProps) {
  const navigate = useNavigate()
  const listId = useId()

  const [query, setQuery] = useState(initialValue)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Whether the user has interacted. Without this, a field rendered with an
  // `initialValue` (the search page's mobile bar) fetches suggestions on mount
  // and drops the panel over the results the visitor came to read.
  const touchedRef = useRef(false)

  /* -- Fetch suggestions -------------------------------------------------- */
  useEffect(() => {
    if (query.trim().length < MIN_QUERY) {
      // Abort whatever is still in flight, or its late response re-populates
      // and reopens the panel over a field the user has just cleared.
      abortRef.current?.abort()
      setSuggestions([])
      setOpen(false)
      setLoading(false)
      return
    }

    const timer = window.setTimeout(async () => {
      // Cancel the previous request. Otherwise a slow response for "ce" can
      // land after a fast one for "cement" and overwrite it.
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      try {
        const results = await searchApi.suggest(query.trim(), controller.signal)
        setSuggestions(results)
        setOpen(touchedRef.current && results.length > 0)
        setHighlighted(-1)
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setSuggestions([])
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [query])

  useEffect(() => () => abortRef.current?.abort(), [])

  /* -- Dismiss on outside click ------------------------------------------- */
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  /* -- ⌘K / Ctrl-K -------------------------------------------------------- */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const go = useCallback(
    (suggestion: Suggestion) => {
      setOpen(false)
      navigate(TYPE_PATH[suggestion.type](suggestion.slug))
    },
    [navigate],
  )

  const submit = useCallback(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    setOpen(false)
    navigate(`/search?q=${encodeURIComponent(trimmed)}`)
  }, [navigate, query])

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setHighlighted((index) => Math.min(index + 1, suggestions.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((index) => Math.max(index - 1, -1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      // Enter on a highlighted suggestion opens it; Enter on free text runs a
      // full search. Both are what the user means.
      if (highlighted >= 0 && suggestions[highlighted]) go(suggestions[highlighted])
      else submit()
    } else if (event.key === 'Escape') {
      setOpen(false)
      setHighlighted(-1)
    }
  }

  const large = size === 'lg'

  return (
    <div ref={containerRef} className={cx('relative', className)}>
      {/* A real form, so Enter submits and a screen reader announces it as
          search — the same contract the catalogue's plain GET form offers. */}
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
        className="flex w-full"
      >
        <div className="relative flex-1 min-w-0">
          <SearchIcon
            size={large ? 18 : 15}
            className={cx(
              'pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]',
              large && 'left-5',
            )}
          />
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={highlighted >= 0 ? `${listId}-${highlighted}` : undefined}
            autoComplete="off"
            autoFocus={autoFocus}
            value={query}
            placeholder={placeholder}
            onChange={(event) => {
              touchedRef.current = true
              setQuery(event.target.value)
            }}
            onFocus={() => {
              touchedRef.current = true
              if (suggestions.length > 0) setOpen(true)
            }}
            onKeyDown={onKeyDown}
            className={cx(
              // The pill: rounded on the left only, because the button closes
              // the right-hand end. `border-r-0` so the two do not show a
              // double edge where they meet.
              'w-full rounded-l-full border border-r-0 border-[var(--border-default)]',
              'bg-[var(--surface-sunken)]',
              'text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]',
              'outline-none transition-colors',
              'focus:border-[var(--accent)]',
              // Chrome's own clear button collides with the loading spinner.
              '[&::-webkit-search-cancel-button]:hidden',
              large ? 'h-14 pl-12 pr-12 text-base' : 'h-10 pl-10 pr-10 text-sm',
            )}
          />

          {loading ? (
            <Spinner
              className={cx(
                'absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-[var(--text-tertiary)]',
                large && 'right-4',
              )}
            />
          ) : null}
        </div>

        <button
          type="submit"
          className={cx(
            'shrink-0 rounded-r-full border border-[var(--accent)] bg-[var(--accent)]',
            'font-medium text-white transition-colors',
            'hover:bg-[var(--accent-hover)] hover:border-[var(--accent-hover)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/40',
            large ? 'h-14 px-6 text-base' : 'h-10 px-4 text-sm',
          )}
        >
          Search
        </button>
      </form>

      {open && suggestions.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className={cx(
            'absolute z-50 mt-2 w-full overflow-hidden rounded-xl border',
            'border-[var(--border-subtle)] bg-[var(--surface-raised)]',
            'shadow-[var(--shadow-overlay)] animate-in',
          )}
        >
          {suggestions.map((suggestion, index) => {
            const IconComponent = TYPE_ICON[suggestion.type]
            return (
              <li
                key={`${suggestion.type}-${suggestion.slug}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === highlighted}
                onPointerDown={(event) => {
                  // pointerdown, not click: the input's blur would close the
                  // list before a click could land.
                  event.preventDefault()
                  go(suggestion)
                }}
                onPointerEnter={() => setHighlighted(index)}
                className={cx(
                  'flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                  index === highlighted
                    ? 'bg-[var(--surface-hover)]'
                    : 'hover:bg-[var(--surface-hover)]',
                )}
              >
                <IconComponent size={15} className="shrink-0 text-[var(--text-tertiary)]" />
                <span className="min-w-0 flex-1 truncate text-[var(--text-primary)]">
                  {suggestion.label}
                </span>
                <span className="shrink-0 text-2xs text-[var(--text-tertiary)]">
                  {suggestion.meta}
                </span>
              </li>
            )
          })}

          <li
            onPointerDown={(event) => {
              event.preventDefault()
              submit()
            }}
            className="flex cursor-pointer items-center gap-3 border-t border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-3 py-2.5 text-sm hover:bg-[var(--surface-hover)]"
          >
            <SearchIcon size={15} className="shrink-0 text-[var(--text-tertiary)]" />
            <span className="text-[var(--text-secondary)]">
              Search everything for “<span className="text-[var(--text-primary)]">{query}</span>”
            </span>
          </li>
        </ul>
      ) : null}
    </div>
  )
}
